import { useState, useEffect, useCallback } from 'react'
import { DollarSign, Plus, Trash2, X, Save } from 'lucide-react'
import { apiService } from '../services/api'
import ProductGroupSelector from '../components/ui/ProductGroupSelector'

const OPERATORS = ['ORANGE', 'PLUS', 'TMOBILE', 'P4']

export default function MarginManagementPage() {
  const [margins, setMargins] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [filterOperator, setFilterOperator] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = filterOperator ? { operator_code: filterOperator } : {}
      const res = await apiService.getMargins(params)
      setMargins(res.data)
    } catch (err) {
      setError('Nie udało się załadować marż')
    } finally {
      setLoading(false)
    }
  }, [filterOperator])

  useEffect(() => { load() }, [load])

  const handleDelete = async (id) => {
    try {
      await apiService.deleteMargin(id)
      setDeleteConfirm(null)
      await load()
    } catch (err) {
      alert('Błąd: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handleCreate = async (data) => {
    try {
      await apiService.createMargin(data)
      setShowModal(false)
      await load()
    } catch (err) {
      alert('Błąd: ' + (err.response?.data?.detail || err.message))
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-8 h-8 text-green-600" />
            Marże per grupa produktów
          </h1>
          <p className="text-gray-600 mt-1">Średnia marża jednostkowa (PLN/szt.) z datami obowiązywania</p>
        </div>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
          <Plus className="w-5 h-5" /> Dodaj marżę
        </button>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
            <select value={filterOperator} onChange={e => setFilterOperator(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="">Wszyscy</option>
              {OPERATORS.map(op => <option key={op} value={op}>{op}</option>)}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      ) : margins.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          Brak zdefiniowanych marż
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Operator</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grupa</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Marża (PLN/szt.)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Od</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Do</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Akcje</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {margins.map(m => (
                <tr key={m.id} className={`hover:bg-gray-50 ${m.valid_to ? 'opacity-50' : ''}`}>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">{m.operator_code}</td>
                  <td className="px-6 py-3 text-sm text-gray-700">{m.product_group}</td>
                  <td className="px-6 py-3 text-sm text-right font-semibold text-green-700">
                    {m.unit_margin.toLocaleString('pl-PL')} PLN
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">{m.valid_from}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{m.valid_to || <span className="text-green-600 font-medium">aktywna</span>}</td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => setDeleteConfirm(m)} className="text-red-600 hover:text-red-800">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <MarginModal onSave={handleCreate} onClose={() => setShowModal(false)} />}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Usunąć marżę?</h3>
            <p className="text-gray-600 mb-6">
              {deleteConfirm.operator_code} / {deleteConfirm.product_group}: {deleteConfirm.unit_margin} PLN (od {deleteConfirm.valid_from})
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 bg-gray-100 rounded-lg">Anuluj</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="px-4 py-2 bg-red-600 text-white rounded-lg">Usuń</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function MarginModal({ onSave, onClose }) {
  const [form, setForm] = useState({
    operator_code: 'ORANGE',
    product_group: null,
    unit_margin: '',
    valid_from: new Date().toISOString().slice(0, 7) + '-01',
  })

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!form.product_group || !form.unit_margin) {
      alert('Wypełnij wszystkie pola')
      return
    }
    onSave({
      operator_code: form.operator_code,
      product_group: form.product_group,
      unit_margin: parseFloat(form.unit_margin),
      valid_from: form.valid_from,
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Nowa marża</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
            <select value={form.operator_code} onChange={e => set('operator_code', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              {OPERATORS.map(op => <option key={op} value={op}>{op}</option>)}
            </select>
          </div>
          <ProductGroupSelector
            operatorCode={form.operator_code}
            value={form.product_group}
            onChange={v => set('product_group', v)}
            showAll={false}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Marża jednostkowa (PLN/szt.)</label>
            <input type="number" step="0.01" min="0" value={form.unit_margin}
              onChange={e => set('unit_margin', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="np. 40.00" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Obowiązuje od</label>
            <input type="date" value={form.valid_from}
              onChange={e => set('valid_from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
            <p className="text-xs text-gray-500 mt-1">Poprzednia marża zostanie automatycznie zamknięta</p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-lg">Anuluj</button>
            <button type="submit" className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Save className="w-4 h-4" /> Zapisz
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
